import { Injectable } from '@angular/core';
import { OrganizationQuery } from '@core/organization/organization.query';
import { Organization } from '@core/models/organization.model';
import { Partner } from '@core/models/partner.model';
import { GpOwnFundDdqQuota, Subscription } from '@core/models/subscription.model';
import { AppStoreQuery } from '@core/state/app-state.query';
import { ThemeSwitcherService } from '@feature/theme-manager/theme-switcher/theme-switcher.service';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { hasActiveGpAddonDd } from './gp-addon-access.util';
import { SubscriptionQuery } from './subscription.query';
import { SubscriptionService } from './subscription.service';

@Injectable({ providedIn: 'root' })
export class SubscriptionAccessService {
  constructor(
    private organizationQuery: OrganizationQuery,
    private subscriptionService: SubscriptionService,
    private subscriptionQuery: SubscriptionQuery,
    private appStoreQuery: AppStoreQuery,
    private themeSwitcherService: ThemeSwitcherService,
  ) {}

  /**
   * Subscription for the chosen organization; emits `null` when there is no active org.
   * Single implementation shared by header + profile so behaviour cannot drift.
   */
  selectChosenOrganizationSubscription(): Observable<Subscription | null> {
    return this.organizationQuery.selectChosenOrganization().pipe(
      distinctUntilChanged((a, b) => this.sameChosenOrganization(a, b)),
      switchMap((org) =>
        org == null
          ? of(null)
          : this.subscriptionService.getCurrentSubscription(false).pipe(
              catchError(() => of(null)),
              take(1),
              switchMap(() => this.subscriptionQuery.selectCurrentSubscription()),
            ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Organization-aware current subscription stream.
   * Refreshes current subscription when chosen organization changes.
   */
  selectCurrentSubscriptionByOrganization(): Observable<Subscription | null> {
    return this.organizationQuery.selectChosenOrganization().pipe(
      filter((org) => !!org),
      distinctUntilChanged((a, b) => this.sameChosenOrganization(a, b)),
      switchMap(() =>
        this.subscriptionService.getCurrentSubscription(false).pipe(
          catchError(() => of(null)),
          switchMap(() => this.subscriptionQuery.selectCurrentSubscription()),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private sameChosenOrganization(a: Organization | null | undefined, b: Organization | null | undefined): boolean {
    if (a == null && b == null) {
      return true;
    }
    if (a == null || b == null) {
      return false;
    }
    const idA = a.id;
    const idB = b.id;
    if (idA != null || idB != null) {
      return idA === idB;
    }
    return a === b;
  }

  selectIsLpFreeAccess(): Observable<boolean> {
    return this.selectCurrentSubscriptionByOrganization().pipe(
      map((subscription) => this.isLpFreeSubscription(subscription)),
    );
  }

  isLpFreeSubscription(subscription: Subscription | null): boolean {
    const tierType = (subscription?.tier?.tierType ?? '').toString().toLowerCase();
    const code = (subscription?.tier?.code ?? '').toString().toLowerCase();
    return tierType === 'lp' && code === 'lp_free';
  }

  /**
   * LP tiers without custom/adapt template rights (matches backend feature limits:
   * `lp_free` and `lp_standard` have `can_use_custom_templates` / `can_adapt_templates` false).
   */
  isLpTemplateManagementLocked(subscription: Subscription | null): boolean {
    const tierType = (subscription?.tier?.tierType ?? '').toString().toLowerCase();
    const code = (subscription?.tier?.code ?? '').toString().toLowerCase();
    if (tierType !== 'lp') {
      return false;
    }
    return code === 'lp_free' || code === 'lp_standard';
  }

  selectIsLpTemplateManagementLocked(): Observable<boolean> {
    return this.selectCurrentSubscriptionByOrganization().pipe(
      map((subscription) => this.isLpTemplateManagementLocked(subscription)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** GP issuer without active `gp_addon_due_diligence` — custom DD template type is locked. */
  selectIsGpCustomTemplateLocked(): Observable<boolean> {
    return combineLatest([this.selectIsGpIssuerOrganization(), this.selectCurrentSubscriptionByOrganization()]).pipe(
      map(([isGpIssuer, subscription]) => isGpIssuer && !hasActiveGpAddonDd(subscription)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * True when the effective partner (from stored theme + organization partners) has due-diligence enabled.
   * Re-evaluates when the chosen organization or active partner theme changes.
   */
  selectDueDiligencePartnerFeaturesEnabled(): Observable<boolean> {
    return this.selectPartnerCapabilityContext().pipe(
      map(({ theme, org }) => this.resolveDueDiligenceEnabled(theme, org)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * True when the effective partner has marketplace enabled (`isMarketplace`).
   * Resolution mirrors {@link selectDueDiligencePartnerFeaturesEnabled} (theme + org partners + keys).
   * Defaults to true when the flag is unset so existing marketplace partners keep current nav behavior.
   */
  selectMarketplacePartnerFeaturesEnabled(): Observable<boolean> {
    return this.selectPartnerCapabilityContext().pipe(
      map(({ theme, org }) => this.resolveMarketplaceEnabled(theme, org)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Emits whenever partner capability inputs change: chosen organization or active theme partner.
   * Partner switches within the same org only update `activeTheme` / `keys`, so org-only streams stay stale.
   */
  private selectPartnerCapabilityContext(): Observable<{ org: Organization | null; theme: Partner | null }> {
    return combineLatest([
      this.organizationQuery.selectChosenOrganization().pipe(
        map((orgFromStream) => {
          // Only fall back to getActive() when the stream has not emitted yet (`undefined`).
          // When the store clears the active org it emits `null` — do not substitute getActive().
          if (orgFromStream !== undefined) {
            return orgFromStream;
          }
          return (this.organizationQuery.getActive() as Organization | undefined) ?? null;
        }),
      ),
      this.themeSwitcherService.activeTheme$.pipe(
        map((theme) => theme ?? this.readStoredActiveTheme()),
        distinctUntilChanged((a, b) => this.samePartnerCapabilityTheme(a, b)),
      ),
    ]).pipe(
      map(([org, theme]) => ({ org, theme })),
      distinctUntilChanged(
        (a, b) => a.org?.id === b.org?.id && this.samePartnerCapabilityTheme(a.theme, b.theme),
      ),
    );
  }

  private samePartnerCapabilityTheme(a: Partner | null, b: Partner | null): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    return (
      a.uid === b.uid &&
      this.readPartnerDueDiligenceFlag(a) === this.readPartnerDueDiligenceFlag(b) &&
      this.readPartnerMarketplaceFlag(a) === this.readPartnerMarketplaceFlag(b)
    );
  }

  /**
   * True when partner is DD-capable but not marketplace-capable (DD-only org).
   * Used to hide marketplace nav (Marketplace, Baskets, Dataroom, Transactions).
   */
  selectIsDueDiligenceOnlyPartner(): Observable<boolean> {
    return combineLatest([
      this.selectDueDiligencePartnerFeaturesEnabled(),
      this.selectMarketplacePartnerFeaturesEnabled(),
    ]).pipe(
      map(([ddEnabled, marketplaceEnabled]) => ddEnabled && !marketplaceEnabled),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Robust DD-only partner detection for gating every marketplace surface (nav tabs + route guard).
   *
   * {@link selectIsDueDiligenceOnlyPartner} builds on {@link resolvePartnerCapabilityFlag}, which
   * fails *open* — it defaults `isMarketplace` to `true` when the active partner cannot be resolved.
   * For a client created by a DD-only issuer no partner record surfaces an explicit
   * `is_marketplace=false`, so marketplace resolved to `true`: the Marketplace tab rendered, the
   * guard skipped its redirect, and the click reached the backend and returned 403.
   *
   * This variant additionally trusts the logged-in user's `ddPartner` flags and the persisted
   * `activeTheme` / `keys`, so DD-only clients are detected even when subscription resolution
   * defaults marketplace on. Both the nav and the guard must consume this so their views agree.
   */
  selectIsDueDiligenceOnlyPartnerContext(): Observable<boolean> {
    return combineLatest([this.selectIsDueDiligenceOnlyPartner(), this.appStoreQuery.selectUser()]).pipe(
      map(([fromService, user]) => {
        if (fromService) {
          return true;
        }
        const ddPartner = user?.ddPartner;
        if (this.readPartnerDueDiligenceFlag(ddPartner) === true && this.readPartnerMarketplaceFlag(ddPartner) !== true) {
          return true;
        }
        return this.isActiveThemeDueDiligenceOnly() || this.isKeysDueDiligenceOnly();
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * True when partner allows colleague invite/manage flows (DD and/or marketplace).
   */
  selectColleagueManagementPartnerFeaturesEnabled(): Observable<boolean> {
    return combineLatest([
      this.selectDueDiligencePartnerFeaturesEnabled(),
      this.selectMarketplacePartnerFeaturesEnabled(),
    ]).pipe(
      map(([ddEnabled, marketplaceEnabled]) => ddEnabled || marketplaceEnabled),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Subscription tiers / upgrade marketing (profile, user menu, dialogs) — hidden when partner is not DD-enabled.
   */
  selectShowSubscriptionMarketingUi(): Observable<boolean> {
    return this.selectDueDiligencePartnerFeaturesEnabled();
  }

  /**
   * LP Free “upgrade” links / tooltips — shown only when the user is on lp_free and partner allows DD marketing.
   * Editing locks still use {@link selectIsLpFreeAccess} alone.
   */
  selectShowLpFreeUpgradePrompt(): Observable<boolean> {
    return combineLatest([this.selectIsLpFreeAccess(), this.selectDueDiligencePartnerFeaturesEnabled()]).pipe(
      map(([isLpFree, ddPartner]) => isLpFree && ddPartner),
      distinctUntilChanged(),
    );
  }

  /**
   * Remaining DDQ runs from profile usage stored in app-state.
   * Unlimited mode is represented by `null` or `-1`.
   */
  selectRemainingDdqRunsFromProfile(): Observable<number | null | undefined> {
    return this.appStoreQuery.selectUserProfile().pipe(
      map((profile) => profile?.subscription?.usage?.remainingDdqRuns),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * True when remaining DDQ runs are exhausted and not unlimited.
   */
  selectIsDdqRunsExhaustedFromProfile(): Observable<boolean> {
    return this.selectRemainingDdqRunsFromProfile().pipe(
      map((remainingDdqRuns) => {
        if (remainingDdqRuns === null || remainingDdqRuns === -1 || remainingDdqRuns === undefined) {
          return false;
        }
        return remainingDdqRuns <= 0;
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * IM generation entitlement from profile usage.
   * Returns true when missing to avoid false-negative blocking.
   */
  selectCanGenerateImFromProfile(): Observable<boolean> {
    return this.appStoreQuery.selectUserProfile().pipe(
      map((profile) => profile?.subscription?.usage?.canGenerateIm),
      map((canGenerateIm) => (canGenerateIm === false ? false : true)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Chosen org is an issuer (emittent company) context. */
  selectIsGpIssuerOrganization(): Observable<boolean> {
    return this.organizationQuery.selectChosenOrganizationType().pipe(
      map((type) => type === 'emittent'),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  selectGpOwnFundDdqQuota(): Observable<GpOwnFundDdqQuota | null> {
    return this.subscriptionQuery.selectCurrentSubscription().pipe(
      map((s) => s?.gpOwnFundDdqQuota ?? null),
      distinctUntilChanged(
        (a, b) =>
          a === b ||
          (!!a &&
            !!b &&
            a.limit === b.limit &&
            a.usedThisMonth === b.usedThisMonth &&
            a.remaining === b.remaining &&
            a.month === b.month &&
            a.year === b.year),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * True when GP monthly own-fund DDQ cap is reached (`remaining === 0`).
   * False when not issuer or quota not loaded yet.
   */
  selectIsGpOwnFundDdqQuotaExhausted(): Observable<boolean> {
    return combineLatest([this.selectIsGpIssuerOrganization(), this.selectGpOwnFundDdqQuota()]).pipe(
      map(([isGp, quota]) => Boolean(isGp && quota && quota.remaining <= 0)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private readStoredActiveTheme(): Partner | null {
    try {
      const raw = localStorage.getItem('activeTheme');
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as Partner;
    } catch {
      return null;
    }
  }

  private resolveDueDiligenceEnabled(theme: Partner | null, org: Organization | null): boolean {
    return this.resolvePartnerCapabilityFlag({
      theme,
      org,
      keysOverrideEnabled: this.readKeysDueDiligenceFlag() && this.keysPartnerMatchesActiveTheme(theme),
      readPartnerFlag: (partner) => this.readPartnerDueDiligenceFlag(partner),
      readThemeFlag: (activeTheme) => this.readPartnerDueDiligenceFlag(activeTheme),
    });
  }

  private resolveMarketplaceEnabled(theme: Partner | null, org: Organization | null): boolean {
    return this.resolvePartnerCapabilityFlag({
      theme,
      org,
      keysOverrideEnabled: this.readKeysMarketplaceFlag() && this.keysPartnerMatchesActiveTheme(theme),
      readPartnerFlag: (partner) => this.readPartnerMarketplaceFlag(partner),
      readThemeFlag: (activeTheme) => this.readPartnerMarketplaceFlag(activeTheme),
    });
  }

  /**
   * Only trust `keys.isDueDiligence` / `keys.isMarketplace` when they refer to the active partner.
   * Prevents stale keys from a previously selected partner forcing marketplace on for DD-only partners.
   */
  private keysPartnerMatchesActiveTheme(theme: Partner | null): boolean {
    const keysUid = this.readKeysPartnerUid();
    if (!keysUid) {
      return false;
    }
    const themeUid = theme?.uid ?? null;
    return !!themeUid && keysUid === themeUid;
  }

  private resolvePartnerCapabilityFlag(options: {
    theme: Partner | null;
    org: Organization | null;
    keysOverrideEnabled: boolean;
    readPartnerFlag: (partner: Partner | null | undefined) => boolean | undefined;
    readThemeFlag: (theme: Partner) => boolean | undefined;
  }): boolean {
    if (options.keysOverrideEnabled) {
      return true;
    }

    if (!options.theme?.uid) {
      return true;
    }

    const partners = options.org?.partners;
    if (partners?.length) {
      const matched = partners.find((p) => p?.uid === options.theme.uid);
      const matchedFlag = options.readPartnerFlag(matched);
      if (typeof matchedFlag === 'boolean') {
        return matchedFlag;
      }

      const keysUid = this.readKeysPartnerUid();
      if (keysUid) {
        const keysMatch = partners.find((p) => p?.uid === keysUid);
        const keysMatchFlag = options.readPartnerFlag(keysMatch);
        if (typeof keysMatchFlag === 'boolean') {
          return keysMatchFlag;
        }
      }

      const currentOrigin = this.normalizeOriginUrl(`${window.location.protocol}//${window.location.hostname}`);
      const byDomain = partners.find((p) => this.normalizeOriginUrl(p?.domain) === currentOrigin);
      const byDomainFlag = options.readPartnerFlag(byDomain);
      if (typeof byDomainFlag === 'boolean') {
        return byDomainFlag;
      }

      if (partners.length === 1) {
        const singleFlag = options.readPartnerFlag(partners[0]);
        if (typeof singleFlag === 'boolean') {
          return singleFlag;
        }
      }

      const enabledPartners = partners.filter((p) => options.readPartnerFlag(p) === true);
      if (enabledPartners.length === 1) {
        return true;
      }
    }

    const themeFlag = options.readThemeFlag(options.theme);
    if (typeof themeFlag === 'boolean') {
      return themeFlag;
    }

    return true;
  }

  private readPartnerDueDiligenceFlag(partner: Partner | null | undefined): boolean | undefined {
    if (!partner) {
      return undefined;
    }
    const raw = partner as Partner & { is_due_diligence?: boolean };
    if (typeof raw.isDueDiligence === 'boolean') {
      return raw.isDueDiligence;
    }
    if (typeof raw.is_due_diligence === 'boolean') {
      return raw.is_due_diligence;
    }
    return undefined;
  }

  private readPartnerMarketplaceFlag(partner: Partner | null | undefined): boolean | undefined {
    if (!partner) {
      return undefined;
    }
    const raw = partner as Partner & { is_marketplace?: boolean };
    if (typeof raw.isMarketplace === 'boolean') {
      return raw.isMarketplace;
    }
    if (typeof raw.is_marketplace === 'boolean') {
      return raw.is_marketplace;
    }
    return undefined;
  }

  private normalizeOriginUrl(url: string | null | undefined): string | null {
    if (!url?.trim()) {
      return null;
    }
    return url.trim().replace(/\/+$/, '');
  }

  private readKeysDueDiligenceFlag(): boolean {
    try {
      const raw = localStorage.getItem('keys');
      if (!raw) {
        return false;
      }
      const parsed = JSON.parse(raw) as { isDueDiligence?: boolean | string };
      return parsed?.isDueDiligence === true || parsed?.isDueDiligence === 'true';
    } catch {
      return false;
    }
  }

  private readKeysMarketplaceFlag(): boolean {
    try {
      const raw = localStorage.getItem('keys');
      if (!raw) {
        return false;
      }
      const parsed = JSON.parse(raw) as { isMarketplace?: boolean | string };
      return parsed?.isMarketplace === true || parsed?.isMarketplace === 'true';
    } catch {
      return false;
    }
  }

  private readKeysPartnerUid(): string | null {
    try {
      const raw = localStorage.getItem('keys');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { partneruid?: string };
      return parsed?.partneruid ?? null;
    } catch {
      return null;
    }
  }

  /** DD-only signal from the persisted `activeTheme` partner (DD enabled, marketplace not). */
  private isActiveThemeDueDiligenceOnly(): boolean {
    const theme = this.readStoredActiveTheme();
    if (!theme) {
      return false;
    }
    return this.readPartnerDueDiligenceFlag(theme) === true && this.readPartnerMarketplaceFlag(theme) !== true;
  }

  /** DD-only signal from the persisted `keys` blob (DD enabled, marketplace not). */
  private isKeysDueDiligenceOnly(): boolean {
    return this.readKeysDueDiligenceFlag() && !this.readKeysMarketplaceFlag();
  }
}
